import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const Problem = () => {
  const { t } = useLanguage();

  const icons = [
    (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
    ),
    (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section className="section-padding bg-gradient-to-b from-white via-watrly-cream/50 to-watrly-sand-light/30 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-watrly-desert/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-watrly-sand-light to-transparent" />
      
      <div className="container-watrly">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center mb-8 md:mb-12 lg:mb-16"
        >
          <span className="inline-block text-xs md:text-sm font-semibold text-watrly-turquoise uppercase tracking-widest mb-2 md:mb-4">
            {t.problem.badge}
          </span>
          <h2 className="heading-section mb-3 md:mb-6">
            {t.problem.title}
          </h2>
          <p className="text-body-lg">
            {t.problem.subtitle}
          </p>
        </motion.div>

        {/* Mobile: Compact 2-column grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid grid-cols-2 gap-3 lg:hidden"
        >
          {t.problem.problems.map((problem, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="p-3 rounded-xl bg-white/80 backdrop-blur-sm border border-watrly-slate/10"
            >
              <div className="w-8 h-8 rounded-lg bg-watrly-turquoise/10 flex items-center justify-center text-watrly-turquoise mb-2">
                {icons[index]}
              </div>
              <h3 className="text-sm font-semibold text-watrly-slate mb-1">
                {problem.title}
              </h3>
              <p className="text-xs text-watrly-slate/60 leading-relaxed line-clamp-2">
                {problem.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Desktop: Original grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="hidden lg:grid lg:grid-cols-2 gap-6"
        >
          {t.problem.problems.map((problem, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group relative p-8 rounded-3xl bg-watrly-sand-light/50 border border-watrly-slate/5 hover:border-watrly-turquoise/20 transition-all duration-500 hover:shadow-xl hover:shadow-watrly-turquoise/5"
            >
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-watrly-turquoise/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl bg-watrly-turquoise/10 flex items-center justify-center text-watrly-turquoise mb-4 group-hover:bg-watrly-turquoise group-hover:text-white transition-all duration-300">
                  {icons[index]}
                </div>
                <h3 className="text-lg font-semibold text-watrly-slate mb-2">
                  {problem.title}
                </h3>
                <p className="text-base text-watrly-slate/60 leading-relaxed">
                  {problem.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-8 md:mt-12 lg:mt-16 text-center"
        >
          <div className="inline-flex items-center gap-2 md:gap-3 px-4 md:px-6 py-3 md:py-4 rounded-xl md:rounded-2xl bg-watrly-turquoise/5 border border-watrly-turquoise/10">
            <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-watrly-turquoise animate-pulse" />
            <span className="text-sm md:text-base text-watrly-slate font-medium">
              {t.problem.bottomBadge}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Problem;
